import Phaser from "phaser";

import type { FoodRuntime } from "../application/food-runtime";
import type { BotRuntime } from "../application/bot-runtime";
import type { FirstRunHint } from "../application/first-run-hint";
import type { GameFlow } from "../application/game-flow";
import type { PlayerRuntime } from "../application/player-runtime";
import type { TailClipRuntime } from "../application/tail-clip-runtime";
import type { BonusRuntime } from "../application/bonus-runtime";
import type { CoinRewardFeedbackPort } from "../application/coin-reward-feedback";
import type { MotionProfile } from "../config/motion-config";
import type { ArenaBackgroundId } from "../config/arena-background-config";
import type { JoystickSafeAreaInsets } from "./virtual-joystick-input";
import type { SeasonalChallengeDescriptor } from "../config/seasonal-challenge-config";
import { createGameConfig } from "./game-config";
import { BootScene } from "./boot-scene";
import { InactiveRenderLifecycle } from "./inactive-render-lifecycle";

export const startGame = (
  flow: GameFlow,
  runtime: PlayerRuntime,
  foodRuntime: FoodRuntime,
  botRuntime: BotRuntime,
  firstRunHint: FirstRunHint,
  getMotionProfile: () => MotionProfile,
  bonusRuntime: BonusRuntime,
  tailClipRuntime?: TailClipRuntime,
  coinRewardFeedback?: CoinRewardFeedbackPort,
  getArenaBackgroundId?: () => ArenaBackgroundId,
  getSafeAreaInsets?: () => JoystickSafeAreaInsets,
  getSeasonalArenaDescriptor?: () => SeasonalChallengeDescriptor | null,
  getHighContrastPreference?: () => boolean,
): Phaser.Game => {
  const config = createGameConfig(
    runtime,
    foodRuntime,
    botRuntime,
    firstRunHint,
    getMotionProfile,
    bonusRuntime,
    tailClipRuntime,
    coinRewardFeedback,
    getArenaBackgroundId,
    getSafeAreaInsets,
    getSeasonalArenaDescriptor,
    getHighContrastPreference,
  );
  const scene = config.scene as BootScene;
  return new Phaser.Game({
    ...config,
    callbacks: {
      postBoot: (game) => {
        const renderLifecycle = new InactiveRenderLifecycle(
          flow,
          game.loop,
          scene,
          undefined,
          {
            refresh: () => game.scale.refresh(),
            source: window,
          },
        );
        game.events.once(Phaser.Core.Events.DESTROY, () => {
          renderLifecycle.destroy();
        });
      },
    },
  });
};
